import { FC } from 'react';
import { AgentType } from '@/types/agents';
import { AdminAgentForm } from '@/types/adminAgent';
import EmojiAvatarPicker from '@/components/EmojiAvatarPicker';

interface Props {
  form: AdminAgentForm;
  onChange: (patch: Partial<AdminAgentForm>) => void;
  /** Limita opções de tipo (curator só RAG). */
  allowSql?: boolean;
  /** Modelos disponíveis no dropdown. */
  models?: string[];
}

const DEFAULT_MODELS = ['gpt-5.4-nano', 'gpt-5.4-mini', 'gpt-5.4'];

const DataTab: FC<Props> = ({ form, onChange, allowSql = true, models = DEFAULT_MODELS }) => {
  return (
    <div className="space-y-5" data-testid="tab-data">
      <div className="flex gap-4 items-start">
        <EmojiAvatarPicker value={form.avatar} onChange={(v) => onChange({ avatar: v })} />
        <div className="flex-1 space-y-3">
          <label className="block">
            <span className="text-sm font-semibold text-content-primary block mb-1">Nome</span>
            <input
              type="text"
              data-testid="data-name"
              value={form.name}
              onChange={(e) => onChange({ name: e.target.value })}
              maxLength={100}
              className="w-full rounded-lg bg-surface-secondary border border-surface-border px-3 py-2 text-content-primary focus:border-accent focus:outline-none"
            />
          </label>
          <label className="block">
            <span className="text-sm font-semibold text-content-primary block mb-1">Descrição</span>
            <input
              type="text"
              data-testid="data-description"
              value={form.description}
              onChange={(e) => onChange({ description: e.target.value })}
              className="w-full rounded-lg bg-surface-secondary border border-surface-border px-3 py-2 text-content-primary focus:border-accent focus:outline-none"
            />
          </label>
        </div>
      </div>

      <fieldset>
        <legend className="text-sm font-semibold text-content-primary mb-2">Tipo</legend>
        <div className="flex gap-3">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name="type"
              value="RAG"
              data-testid="data-type-rag"
              checked={form.type === 'RAG'}
              onChange={() => onChange({ type: 'RAG' as AgentType })}
              className="accent-accent"
            />
            <span className="text-sm text-content-secondary">RAG (documentos)</span>
          </label>
          {allowSql && (
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="type"
                value="SQL"
                data-testid="data-type-sql"
                checked={form.type === 'SQL'}
                onChange={() => onChange({ type: 'SQL' as AgentType })}
                className="accent-accent"
              />
              <span className="text-sm text-content-secondary">SQL (banco de dados)</span>
            </label>
          )}
        </div>
      </fieldset>

      <label className="block">
        <span className="text-sm font-semibold text-content-primary block mb-1">Modelo de IA</span>
        <select
          data-testid="data-model"
          value={form.model}
          onChange={(e) => onChange({ model: e.target.value })}
          className="w-full rounded-lg bg-surface-secondary border border-surface-border px-3 py-2 text-content-primary focus:border-accent focus:outline-none"
        >
          {models.map((m) => (
            <option key={m} value={m}>
              {m}
            </option>
          ))}
        </select>
      </label>

      <label className="block">
        <span className="text-sm font-semibold text-content-primary block mb-1">
          Criatividade ({form.temperature.toFixed(1)})
        </span>
        <input
          type="range"
          data-testid="data-temperature"
          min={0}
          max={2}
          step={0.1}
          value={form.temperature}
          onChange={(e) => onChange({ temperature: Number(e.target.value) })}
          className="w-full accent-accent"
        />
        <p className="text-xs text-content-tertiary">0 = preciso · 2 = criativo</p>
      </label>

      <label className="block">
        <span className="text-sm font-semibold text-content-primary block mb-1">
          Personalidade / instruções (system prompt)
        </span>
        <textarea
          data-testid="data-system-prompt"
          value={form.system_prompt}
          onChange={(e) => onChange({ system_prompt: e.target.value })}
          rows={6}
          className="w-full resize-none rounded-lg bg-surface-secondary border border-surface-border px-3 py-2 text-content-primary focus:border-accent focus:outline-none font-mono text-sm"
        />
      </label>
    </div>
  );
};

export default DataTab;
